import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const pollsRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("error"); return; }
    if (pollsRef.current >= MAX_POLLS) { setStatus("pending"); return; }
    pollsRef.current += 1;
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (data.status === "expired") { setStatus("error"); return; }
      setTimeout(poll, 2000);
    } catch {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="min-h-screen bg-[#0b0e17] text-white flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-md">
        {status === "checking" && (<><Loader2 size={56} className="animate-spin text-[#e0574d] mx-auto" /><h1 className="mt-6 text-3xl font-bold font-display">Confirming your order…</h1><p className="mt-2 text-white/50">Hang tight while we verify your payment.</p></>)}
        {status === "paid" && (<><CheckCircle2 size={64} className="text-[#8fe388] mx-auto" /><h1 className="mt-6 text-4xl font-extrabold font-display">Order confirmed!</h1><p className="mt-3 text-white/60">Your uniforms are on the way. Thanks for repping Elite.</p></>)}
        {status === "pending" && (<><Loader2 size={56} className="text-[#e0574d] mx-auto" /><h1 className="mt-6 text-3xl font-bold font-display">Payment processing</h1><p className="mt-2 text-white/50">This is taking a moment. Your order will update automatically once payment settles.</p></>)}
        {status === "error" && (<><XCircle size={64} className="text-[#e0574d] mx-auto" /><h1 className="mt-6 text-3xl font-bold font-display">We couldn't confirm this order</h1><p className="mt-2 text-white/50">If you were charged, it will still be recorded. Please contact your manager.</p></>)}
        <Link to="/portal" data-testid="back-to-portal" className="mt-8 inline-flex items-center gap-2 bg-white text-[#0b0e17] font-bold py-3.5 px-7 rounded-sm hover:bg-white/90 transition-colors">
          Back to Portal <ArrowRight size={18} />
        </Link>
      </motion.div>
    </div>
  );
}
